<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import {
    faArrowsMaximize,
    faList,
    faMinus,
    faPlus,
    faSlidersH,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { GoogleMap, MarkerCluster } from 'vue3-google-map';
import HotelMarker from '../HotelMarker.vue';
import CardSection from './CardSection.vue';
import HotelCard from '@components/util/hotel-card';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import type HotelModule from '@itinerary/hotel/HotelModule';
import { useHandleEvents } from '@composable/_local/hotel&view/useHandleEvents';

const {
    list = [],
    title,
    mapHotelList = [],
    showMap,
    displayBanners,
    forceMobileView,
    preloadFirstImage,
} = defineProps<IProps>();
const emit = defineEmits<{
    closeMap: [];
    viewSection: [];
    openFilters: [];
    loadMore: [];
}>();
const itineraryStore = useItineraryStore();
const { loading } = storeToRefs(itineraryStore);
const currentModule = useCurrentItineraryModule();

interface IProps {
    title?: string;
    list?: HotelPackage[];
    mapHotelList?: HotelPackage[];
    displayBanners?: boolean;
    showMap?: boolean;
    isLoading?: boolean;
    forceMobileView?: boolean;
    preloadFirstImage?: boolean;
    loadInfinite?: { hasNext: boolean };
}
const cardSectionRef = ref<ComponentPublicInstance>();
const mapRef = ref<InstanceType<typeof GoogleMap>>();
const isMounted = useMounted();

const { isMobile } = useSize();
const currentHotelIndex = ref(0);
const zoom = ref(16);
const mapLoaded = ref(false);
const hoveredHotelId = ref<number>();
const root = ref<HTMLDivElement>();
const currentPackage = computed(() => mapHotelList[currentHotelIndex.value]);
const center = computed(() => {
    if (currentPackage.value?.latitude && currentPackage.value.longitude) {
        return {
            lat: currentPackage.value.latitude,
            lng: currentPackage.value.longitude,
        };
    } else {
        return { lat: 32.6380644, lng: 51.6620884 };
    }
});
const filteredList = computed(() => {
    const hotelList = showMap ? mapHotelList : list;
    if (!hoveredHotelId.value || !showMap || !isMobile.value) {
        return hotelList;
    }
    return hotelList?.filter(pkg => pkg.id === hoveredHotelId.value);
});

const pricesLoading = computed(
    () =>
        (currentModule.value as HotelModule).hotelPricesLoading ||
        !isMounted.value
);

const openFullscreen = () => {
    if (root.value?.requestFullscreen) {
        root.value?.requestFullscreen();
    } else if ((root.value as any).webkitRequestFullscreen) {
        (root.value as any).webkitRequestFullscreen();
    } else if ((root.value as any).msRequestFullscreen) {
        (root.value as any).msRequestFullscreen();
    }
};
const closeFullscreen = () => {
    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
    }
};
const increaseZoom = () => {
    if (zoom.value < 32) {
        zoom.value++;
    }
};
const decreaseZoom = () => {
    if (zoom.value > 1) {
        zoom.value--;
    }
};
const onMouseover = (item: HotelPackage) => {
    hoveredHotelId.value = item.id ?? undefined;
    (cardSectionRef.value as any)?.scrollToCard(item.uniqueId);
};
const onMouseleave = () => {
    hoveredHotelId.value = undefined;
};
const onHoverCard = (hotel: HotelPackage) => {
    hoveredHotelId.value = hotel.id ?? undefined;
    if (mapRef.value?.ready) {
        mapRef.value.map?.panTo({
            lat: hotel.latitude || 0,
            lng: hotel.longitude || 0,
        });
    }
};
watch(
    () => showMap,
    val => {
        if (val) {
            if (isMobile.value) {
                hoveredHotelId.value = undefined;
            }
        }
    }
);
watch(
    () => mapRef.value?.ready,
    async ready => {
        if (ready) {
            const orderByDistance = await import('geolib/es/orderByDistance');
            const map = mapRef.value?.map;
            const sortedList = orderByDistance.default(
                currentPackage.value.location,
                mapHotelList.map(a => a.location)
            );
            const bounds = new (window as any).google.maps.LatLngBounds();
            for (let i = 0; i <= 3; i++) {
                const marker = sortedList[i];
                if (marker) {
                    bounds.extend({
                        lat: (marker as any).latitude,
                        lng: (marker as any).longitude,
                    });
                }
            }
            map?.fitBounds(bounds);
            mapLoaded.value = true;
        }
    }
);
const handleEvents = useHandleEvents;
const packageSelectStore = usePackageSelectStore();
const formStore = useFormStore();
const hotelReserve = (hotelItem: HotelPackage, index: number) => {
    handleEvents(hotelItem, index);
    packageSelectStore.clearSelectedPackages();
    formStore.itineraryPackages = [];
};
</script>

<template>
    <div ref="root" :class="{ 'hotels-map-wrapper': showMap }">
        <div :class="{ 'hotels-map-container': showMap }">
            <section v-if="showMap" class="map-section-container">
                <GoogleMap
                    ref="mapRef"
                    class="google-map"
                    :center
                    :zoom="loading ? 6 : zoom"
                    api-key="AIzaSyAGCWxWe1ElfwMJpvMw3n8Xbn0qr8Agsbk"
                    :libraries="['places']"
                    language="fa"
                    :zoom-control="false"
                    :map-type-control="false"
                    :scale-control="false"
                    :street-view-control="false"
                    :rotate-control="false"
                    :fullscreen-control="false"
                    :disable-default-ui="false"
                    gesture-handling="greedy"
                    :clickable-icons="false"
                    @click="onMouseleave"
                >
                    <template #default="{ ready, map }">
                        <MarkerCluster>
                            <template v-if="ready">
                                <HotelMarker
                                    v-for="hotel in mapHotelList"
                                    :key="`${hotel.key}-marker`"
                                    :item="hotel"
                                    :map
                                    :hide-price="forceMobileView"
                                    :is-focused="
                                        hotel && hoveredHotelId === hotel.id
                                    "
                                    @mouseover="() => onMouseover(hotel)"
                                    @click="() => onMouseover(hotel)"
                                    @mouseleave="onMouseleave"
                                />
                            </template>
                        </MarkerCluster>
                    </template>
                </GoogleMap>
                <mr-button
                    class="close-button desktop-only"
                    :icon-button="faXmark"
                    @click="emit('closeMap')"
                />
                <mr-button
                    class="open-fullscreen-button desktop-only"
                    :icon-button="faArrowsMaximize"
                    @click="openFullscreen"
                />
                <mr-button
                    class="close-fullscreen-button desktop-only"
                    :right-icon="faXmark"
                    @click="closeFullscreen"
                >
                    بستن تمام صفحه (ESC)
                </mr-button>

                <div class="map-zoom">
                    <mr-button
                        class="inc-zoom"
                        :icon-button="faPlus"
                        size="big"
                        @click="increaseZoom"
                    />
                    <mr-button
                        class="dec-zoom"
                        :icon-button="faMinus"
                        size="big"
                        @click="decreaseZoom"
                    />
                </div>
            </section>
            <CardSection
                ref="cardSectionRef"
                :title
                :display-banners="!showMap && displayBanners"
                :list="filteredList"
                :class="{
                    'list-section-container': showMap,
                    'hide-in-mobile': !hoveredHotelId,
                }"
                :horizontal-mobile="showMap"
                :vertical-desktop="forceMobileView && !showMap"
                :load-infinite
                @load-more="emit('loadMore')"
                @view-section="emit('viewSection')"
            >
                <template #card="{ item, index }">
                    <slot
                        name="card"
                        :item
                        :index
                        :on-hover-card="
                            (item: HotelPackage) => onHoverCard(item)
                        "
                        :hovered-hotel-id
                        :clear-hovered="() => (hoveredHotelId = undefined)"
                    >
                        <HotelCard
                            :hotel-item="item"
                            :mobile-view="showMap"
                            :is-static="forceMobileView"
                            class="hotel-card"
                            :class="{
                                hovered:
                                    showMap &&
                                    item &&
                                    hoveredHotelId === item.id,
                                'other-hovered':
                                    showMap &&
                                    item &&
                                    hoveredHotelId !== undefined &&
                                    hoveredHotelId !== item.id,
                                mobile: forceMobileView && !showMap,
                            }"
                            mobile-observing
                            :preload-first-image="
                                index === 0 && preloadFirstImage
                            "
                            :prices-loading
                            @reserve="hotelReserve(item, index)"
                            @mouseenter="onHoverCard(item)"
                            @mouseleave="hoveredHotelId = undefined"
                        >
                            <template v-if="forceMobileView" #pricing>
                                <mr-button
                                    v-if="!pricesLoading && !item.payable"
                                    size="medium"
                                    full-width
                                    class="show-price-btn"
                                >
                                    مشاهده قیمت
                                </mr-button>
                            </template>
                        </HotelCard>
                    </slot>
                </template>
            </CardSection>
            <div class="actions-container">
                <div v-if="showMap" class="mobile-actions">
                    <mr-button
                        full-width
                        size="small"
                        :right-icon="faSlidersH"
                        @click="emit('openFilters')"
                    >
                        فیلترها
                    </mr-button>
                    <mr-divider
                        class="mr-divider"
                        vertical
                        thickness="1px"
                        color="var(--blue-300)"
                    />
                    <mr-button
                        full-width
                        size="small"
                        :right-icon="faList"
                        @click="emit('closeMap')"
                    >
                        نمایش لیست
                    </mr-button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
:fullscreen {
    .map-zoom {
        display: flex !important;
    }
    .close-fullscreen-button {
        display: flex !important;
    }
    .close-button,
    .open-fullscreen-button {
        display: none;
    }
}

.actions-container {
    display: none;
    padding: 12px;
    position: fixed;
    bottom: 0;
    width: 100%;
    .mr-divider {
        margin-inline: 20px;
    }
    @include mobile-only {
        display: flex;
    }
    .mobile-actions {
        display: flex;
        width: 100%;
        justify-content: center;
        padding: 8px;
        background-color: usePalette();
        border-radius: 12px;
        @include shadow;
    }
}

.hotels-map-wrapper {
    position: fixed;
    top: 80px;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: usePalette($p-cool-gray, $p-100);
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    @include mobile-only {
        top: 0;
        margin: 0 !important;
    }

    .hotels-map-container {
        display: flex;
        align-items: stretch;
        max-width: $app-container-max-width;
        width: 100%;
        height: 100%;
        padding: 16px 0;
        @include mobile-only {
            justify-content: center;
        }
        .list-section-container {
            max-width: 350px;
            @include mobile-only {
                margin-bottom: 64px;
            }
        }
        .hide-in-mobile {
            @include mobile-only {
                display: none;
            }
        }
        @include mobile-only {
            padding: 0;
        }
    }
}

.map-section-container {
    position: relative;
    flex: 1;
    margin-left: 12px;
    border: 3px solid white;
    border-radius: 12px;

    @include mobile-only {
        margin: 0;
    }

    .close-button {
        position: absolute;
        top: 12px;
        right: 12px;
    }
    .open-fullscreen-button {
        position: absolute;
        top: 12px;
        left: 12px;
    }
    .close-fullscreen-button {
        display: none;
        position: absolute;
        top: 12px;
        right: 12px;
    }
    .map-zoom {
        position: absolute;
        bottom: 40px;
        right: 40px;
        display: none;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        .dec-zoom {
            margin-top: 8px;
        }
    }
}

.list-section-container {
    @include no-scrollbar;
    flex: 1;
    overflow-y: scroll;

    @include mobile-only() {
        position: absolute;
        bottom: 0;
    }
}

.google-map {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 12px;
    :deep(.gm-style) {
        font-feature-settings: 'ss02' !important;
    }
}

.hotel-card {
    @include desktop-only {
        &.other-hovered {
            opacity: 0.5;
        }
        &.hovered {
            opacity: 1;
        }
        &.mobile {
            min-width: 280px;
            max-width: 280px;
        }
    }
    .show-price-btn {
        margin-block: -4px;
    }
}
</style>
